/**
 * Contract and token addresses
 *
 * This file imports from the centralized chain config.
 * All chain data is managed in src/config/chains/
 */
import { CollateralAssetResponse } from "../../codegen-api";
import { ChainIdEnum } from "../../enums/chain";
import {
  CHAIN_CONFIGS,
  ERC20Addresses,
  BridgeAddresses,
  MultichainAddresses,
} from "../../config/chains";

export const DepositWithdrawCollaterals = {
  Usdc: "USDC",
  NativeUsdc: "Native USDC",
  Usdt: "USDT",
  Weth: "WETH",
  Wbtc: "WBTC",
  Mon: "MON",
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DepositWithdrawCollaterals =
  (typeof DepositWithdrawCollaterals)[keyof typeof DepositWithdrawCollaterals];

// Create a const of type CombinedCollaterals
export const AllCollaterals = {
  ...CollateralAssetResponse,
  ...DepositWithdrawCollaterals,
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AllCollaterals =
  | CollateralAssetResponse
  | DepositWithdrawCollaterals;

export const IsUSDC = (asset: AllCollaterals) =>
  asset === DepositWithdrawCollaterals.Usdc ||
  asset === DepositWithdrawCollaterals.NativeUsdc;

export const getAsset = (asset: AllCollaterals) =>
  asset === DepositWithdrawCollaterals.NativeUsdc
    ? DepositWithdrawCollaterals.Usdc
    : asset;

type ERC20AddressesForChainId = {
  [key in ChainIdEnum]?: ERC20Addresses | undefined;
};

/**
 * ERC20_ADDRESSES - now derived from centralized CHAIN_CONFIGS
 * Maintained for backwards compatibility
 */
export const ERC20_ADDRESSES: ERC20AddressesForChainId = Object.fromEntries(
  Array.from(CHAIN_CONFIGS.entries()).map(([chainId, config]) => [
    chainId,
    config.contracts.erc20,
  ])
) as ERC20AddressesForChainId;

export const getCollateralERC20Addresses = (
  asset: AllCollaterals,
  _chainId?: ChainIdEnum
) => {
  // Fall back to current chain from env if chainId not provided
  const chainId = _chainId || (Number(process.env.REACT_APP_CHAIN_ID) as ChainIdEnum) || undefined;
  if (chainId && ERC20_ADDRESSES[chainId]) {
    switch (asset) {
      case CollateralAssetResponse.Usdc:
        return {
          l1: ERC20_ADDRESSES[chainId]?.usdc,
          l2: ERC20_ADDRESSES[chainId]?.usdc,
        };
      case DepositWithdrawCollaterals.NativeUsdc:
        return {
          l1: ERC20_ADDRESSES[chainId]?.["usdc-native"],
          l2:
            ERC20_ADDRESSES[chainId]?.["usdc-native"] ||
            ERC20_ADDRESSES[chainId]?.usdc,
        };
      case CollateralAssetResponse.Usdt:
        return {
          l1: ERC20_ADDRESSES[chainId]?.usdt,
          l2: ERC20_ADDRESSES[chainId]?.usdt,
        };
      case CollateralAssetResponse.Weth:
        return {
          l1: ERC20_ADDRESSES[chainId]?.weth,
          l2: ERC20_ADDRESSES[chainId]?.weth,
        };
      case CollateralAssetResponse.Wbtc:
        return {
          l1: ERC20_ADDRESSES[chainId]?.wbtc,
          l2: ERC20_ADDRESSES[chainId]?.wbtc,
        };
      case DepositWithdrawCollaterals.Mon:
        return {
          l1: ERC20_ADDRESSES[chainId]?.mon,
          l2: ERC20_ADDRESSES[chainId]?.mon,
        };
    }
  }
  return {
    l1: undefined,
    l2: undefined,
  };
};

type ContractAddressesForChainId = {
  [key in ChainIdEnum]?: BridgeAddresses | undefined;
};

/**
 * CONTRACT_ADDRESSES - now derived from centralized CHAIN_CONFIGS
 * Maintained for backwards compatibility
 */
export const CONTRACT_ADDRESSES: ContractAddressesForChainId = Object.fromEntries(
  Array.from(CHAIN_CONFIGS.entries()).map(([chainId, config]) => [
    chainId,
    config.contracts.bridge,
  ])
) as ContractAddressesForChainId;

type IMultichainContractAddresses = {
  [key in
    | ChainIdEnum.OPTIMISM
    | ChainIdEnum.OPTIMISM_TESTNET
    | ChainIdEnum.ARBITRUM
    | ChainIdEnum.ARBITRUM_TESTNET]: MultichainAddresses;
};

/**
 * MULTICHAIN_CONTRACT_ADDRESSES - now derived from centralized CHAIN_CONFIGS
 * Maintained for backwards compatibility
 */
export const MULTICHAIN_CONTRACT_ADDRESSES: IMultichainContractAddresses = {
  [ChainIdEnum.OPTIMISM]: CHAIN_CONFIGS.get(ChainIdEnum.OPTIMISM)!.contracts.multichain!,
  [ChainIdEnum.OPTIMISM_TESTNET]: CHAIN_CONFIGS.get(ChainIdEnum.OPTIMISM_TESTNET)!.contracts.multichain!,
  [ChainIdEnum.ARBITRUM]: CHAIN_CONFIGS.get(ChainIdEnum.ARBITRUM)!.contracts.multichain!,
  [ChainIdEnum.ARBITRUM_TESTNET]: CHAIN_CONFIGS.get(ChainIdEnum.ARBITRUM_TESTNET)!.contracts.multichain!,
};

export const getSocketConnectorAddress = (
  collateral: DepositWithdrawCollaterals,
  type: "deposit" | "withdrawal",
  chainId?: ChainIdEnum
) => {
  const multichainAddresses = chainId
    ? MULTICHAIN_CONTRACT_ADDRESSES[
        chainId as keyof typeof MULTICHAIN_CONTRACT_ADDRESSES
      ]
    : undefined;

  const connector =
    type === "deposit"
      ? multichainAddresses?.connector
      : multichainAddresses?.withdrawalConnector;

  switch (collateral) {
    case CollateralAssetResponse.Usdc:
      return connector?.usdc;
    case DepositWithdrawCollaterals.NativeUsdc:
      return connector?.["native-usdc"];
    case CollateralAssetResponse.Weth:
      return connector?.weth;
  }
  return undefined;
};

export type ICollateralAssetToAddresses = {
  [key in AllCollaterals]: string[];
};

/**
 * Helper function to collect addresses from all chains for a specific token field
 */
const collectAddressesFromChains = (
  fieldGetter: (erc20: ERC20Addresses) => string
): string[] => {
  const addresses: string[] = [];
  CHAIN_CONFIGS.forEach((config) => {
    const address = fieldGetter(config.contracts.erc20);
    if (address) {
      addresses.push(address);
    }
  });
  return addresses;
};

/**
 * Maps collateral types to all possible addresses across chains
 * Derived from centralized CHAIN_CONFIGS
 */
export const addressToDepositCollateral: ICollateralAssetToAddresses = {
  [CollateralAssetResponse.Usdc]: [
    ...collectAddressesFromChains((erc20) => erc20.usdc),
    ...collectAddressesFromChains((erc20) => erc20.l2usdc),
  ],
  [DepositWithdrawCollaterals.NativeUsdc]: [
    ...collectAddressesFromChains((erc20) => erc20["usdc-native"]),
  ],
  [CollateralAssetResponse.Usdt]: [
    ...collectAddressesFromChains((erc20) => erc20.usdt),
    ...collectAddressesFromChains((erc20) => erc20.l2usdt),
  ],
  [CollateralAssetResponse.Weth]: [
    ...collectAddressesFromChains((erc20) => erc20.weth),
    ...collectAddressesFromChains((erc20) => erc20.l2weth),
  ],
  [CollateralAssetResponse.Wbtc]: [
    ...collectAddressesFromChains((erc20) => erc20.wbtc),
    ...collectAddressesFromChains((erc20) => erc20.l2wbtc),
  ],
  [CollateralAssetResponse.Sdai]: [],
  [DepositWithdrawCollaterals.Mon]: [
    ...collectAddressesFromChains((erc20) => erc20.mon || ""),
  ],
};
